import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { connect } from 'react-redux';
import axios from '../../axiosdata';
import * as actions from '../../store/actions/index';

import classes from './Calendar.module.css';
import InfoPopup from './InfoPopup';
import BackdropBlack from '../../UI/Backdrop/BackdropBlack';
import NextButton from '../../UI/Buttons/NextButton';
import PrevButton from '../../UI/Buttons/PrevButton';

const DAYS_OF_WEEK = ['Sunnuntai', 'Maanantai', 'Tiistai', 'Keskiviikko', 'Torstai', 'Perjantai', 'Lauantai'];
const DAYS_PER_PAGE = 5;

const Calendar = ({ isAuthenticated, onChange, refreshKey }) => {
    const [programs, setPrograms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showInfoPopup, setShowInfoPopup] = useState(false);
    const [selectedProgram, setSelectedProgram] = useState(null);
    const [currentPageIndex, setCurrentPageIndex] = useState(0);

    const fetchPrograms = useCallback(() => {
        setLoading(true);
        axios.get('/programs.json')
            .then(res => {
                const programsArray = Object.keys(res.data || {}).map(key => ({
                    ...res.data[key],
                    id: key,
                }));
                setPrograms(programsArray);
                setLoading(false);
            })
            .catch(err => {
                console.error('Error fetching calendar data:', err);
                setLoading(false);
            });
    }, []);

    useEffect(() => {
        fetchPrograms();
    }, [fetchPrograms, refreshKey]);

    // Process and organize programs data
    const processedData = useMemo(() => {
        if (programs.length === 0) return { pages: [], currentProgram: null };

        // Sort programs by date and time
        const sorted = [...programs].sort((a, b) => {
            const dateCompare = new Date(a.program.date).getTime() - new Date(b.program.date).getTime();
            if (dateCompare !== 0) return dateCompare;
            return parseInt(a.program.startTime) - parseInt(b.program.startTime);
        });

        // Group programs by date
        const groupedByDate = sorted.reduce((acc, program) => {
            const date = program.program.date;
            if (!acc[date]) acc[date] = [];
            acc[date].push(program);
            return acc;
        }, {});

        // Convert to array of days
        const days = Object.entries(groupedByDate).map(([date, dayPrograms]) => ({
            date,
            programs: dayPrograms
        }));

        // Chunk days into pages of 5
        const pages = [];
        for (let i = 0; i < days.length; i += DAYS_PER_PAGE) {
            pages.push(days.slice(i, i + DAYS_PER_PAGE));
        }

        // Find current program
        const now = new Date();
        const currentProgram = sorted.find(p => {
            const programDate = new Date(p.program.date);
            if (now.toDateString() !== programDate.toDateString()) return false;

            const [startHour, startMin] = p.program.startTime.split(':').map(Number);
            const [endHour, endMin] = p.program.end_time.split(':').map(Number);

            const startTime = new Date(programDate);
            startTime.setHours(startHour, startMin, 0);

            const endTime = new Date(programDate);
            endTime.setHours(endHour, endMin, 0);

            return now >= startTime && now < endTime;
        });

        return { pages, currentProgram };
    }, [programs]);

    // Update Redux with current program
    useEffect(() => {
        if (processedData.currentProgram) {
            onChange(processedData.currentProgram.program);
        } else {
            onChange({
                programName: 'Ei ohjelmaa',
                groupName: 'Katso ohjelmakalenteri',
                date: ' ',
                startTime: ' ',
                end_time: ' ',
                info: ' '
            });
        }
    }, [processedData.currentProgram, onChange]);

    // Helper functions
    const getWeekDay = useCallback((date) => {
        return DAYS_OF_WEEK[new Date(date).getDay()];
    }, []);

    const calculateHeight = useCallback((startTime, endTime) => {
        const timeDiff = parseInt(endTime.slice(0, 2)) - parseInt(startTime.slice(0, 2));
        const heightMap = {
            1: 'onehour',
            2: 'twohours',
            3: 'threehours',
            4: 'fourhours',
            5: 'fivehours',
            6: 'sixhours',
        };
        return heightMap[timeDiff] || null;
    }, []);

    const isCurrentDay = useCallback((date) => {
        const now = new Date();
        const compareDate = new Date(date);
        return now.toDateString() === compareDate.toDateString();
    }, []);

    const handleOpenInfoPopup = useCallback((program) => {
        setSelectedProgram(program);
        setShowInfoPopup(prev => !prev);
    }, []);

    const handleCloseInfoPopup = useCallback(() => {
        setShowInfoPopup(false);
    }, []);

    const handleNext = useCallback(() => {
        if (currentPageIndex < processedData.pages.length - 1) {
            setCurrentPageIndex(prev => prev + 1);
        }
    }, [currentPageIndex, processedData.pages.length]);

    const handlePrevious = useCallback(() => {
        if (currentPageIndex > 0) {
            setCurrentPageIndex(prev => prev - 1);
        }
    }, [currentPageIndex]);

    if (loading) {
        return <div>Loading...</div>;
    }

    if (processedData.pages.length === 0) {
        return <div>No programs available</div>;
    }

    const currentPage = processedData.pages[currentPageIndex] || [];

    return (
        <div>
            <div className={classes.desktop}>
                <PrevButton click={handlePrevious}>Edellinen</PrevButton>
                <NextButton click={handleNext}>Seuraava</NextButton>
            </div>

            {showInfoPopup && selectedProgram && (
                <div>
                    <InfoPopup
                        program={selectedProgram}
                        isAuthenticated={isAuthenticated}
                        onProgramChanged={() => { handleCloseInfoPopup(); fetchPrograms(); }}
                    />
                    <BackdropBlack
                        show={showInfoPopup}
                        clicked={handleCloseInfoPopup}
                    />
                </div>
            )}

            <div className={classes.calendarWrapper}>
                <div className={classes.calendar}>
                    <div className={classes.calendarInner}>
                        {currentPage.map((day) => (
                            <div className={classes.day} key={day.date}>
                                <div className={classes.header2}></div>
                                <div className={[
                                    classes.header,
                                    isCurrentDay(day.date) ? classes.currentDay : ''
                                ].join(' ')}>
                                    <h2>{getWeekDay(day.date)}</h2>
                                    <h2>{day.date.slice(8,10)}.{day.date.slice(5,7)}</h2>
                                </div>
                                {day.programs.map((program) => (
                                    <div
                                        key={program.id}
                                        className={[
                                            classes.program,
                                            classes[calculateHeight(program.program.startTime, program.program.end_time)]
                                        ].join(' ')}
                                        onClick={() => handleOpenInfoPopup(program)}
                                    >
                                        <h5>{program.program.startTime}-{program.program.end_time}</h5>
                                        <h3>{program.program.programName}</h3>
                                        <h3>
                                            {calculateHeight(program.program.startTime, program.program.end_time) === "onehour"
                                                ? ' '
                                                : program.program.groupName}
                                        </h3>
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

const mapStateToProps = state => {
    return {
        isAuthenticated: state.auth.isAdmin,
    }
};

const mapDispatchToProps = dispatch => {
    return {
        onChange: (program) => dispatch(actions.setCurrentProgram(program))
    }
};

export default connect(mapStateToProps, mapDispatchToProps)(Calendar);
